import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CATEGORIES_OPCVM,
  Decimal,
  LIBELLE_CATEGORIE_OPCVM,
  STATUTS_INSTRUMENT,
  TYPES_INSTRUMENT,
  dateDuJour,
  type CategorieOpcvm,
  type TypeInstrument,
} from '@gsm/partage';
import { useRessource, messageErreur } from '../../api/ressource';
import { api, ErreurApi } from '../../api/client';
import { useAuth } from '../../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../../composants/Tableau';
import { BandeauAlerte } from '../../composants/BandeauAlerte';
import { Pastille, type TonPastille } from '../../composants/Pastille';
import { Nombre } from '../../composants/Nombre';
import { Champ } from '../../composants/Champ';
import { Modale } from '../../composants/Modale';
import { Icone } from '../../composants/Icone';
import {
  BarreFiltres,
  FiltreChoix,
  FiltreRecherche,
  FiltreSegments,
  correspond,
} from '../../composants/Filtres';

interface Instrument {
  id: string;
  code: string;
  isin: string | null;
  libelle: string;
  type: TypeInstrument;
  statut: string;
  chariaCompliant: boolean;
  trancheCourante: string | null;
  action: { ticker: string; secteur: string | null; quotite: number } | null;
  bdt: { dateEcheance: string; tauxFacial: string; nominalUnitaire: string } | null;
  opcvm: {
    societeGestion: string;
    categorie: CategorieOpcvm;
    fondsParDefaut: boolean;
    montantMinSouscription: string;
  } | null;
}

interface RapportImport {
  lignes: number;
  crees: number;
  ignores: number;
  anomalies: { ligne: number; code: string; message: string }[];
}

const TON_STATUT: Record<string, TonPastille> = {
  ACTIF: 'succes',
  A_COMPLETER: 'attente',
  SUSPENDU: 'attente',
  RADIE: 'refus',
};

const LIBELLE_STATUT: Record<string, string> = {
  ACTIF: 'Actif',
  A_COMPLETER: 'À compléter',
  SUSPENDU: 'Suspendu',
  RADIE: 'Radié',
};

/**
 * Saisie vierge. Fonction et non constante : la date du jour est relue à
 * chaque ouverture du formulaire, jamais figée au chargement du module.
 *
 * La date d'échéance reste vide à dessein — un BDT échéant le jour même
 * n'existe pas, et une valeur plausible mais fausse est pire qu'un champ
 * à remplir.
 */
function vide() {
  return {
    type: 'ACTION' as TypeInstrument,
    code: '',
    isin: '',
    libelle: '',
    chariaCompliant: false,
    statut: 'ACTIF',
    // Action
    ticker: '',
    secteur: '',
    quotite: '',
    // BDT
    dateEmission: dateDuJour(),
    dateEcheance: '',
    tauxFacial: '',
    periodiciteCoupon: '',
    nominalUnitaire: '',
    // OPCVM
    societeGestion: '',
    categorie: 'MONETAIRE' as CategorieOpcvm,
    montantMinSouscription: '',
    fractionnement: false,
    fondsParDefaut: false,
  };
}

type Saisie = ReturnType<typeof vide>;

export function Instruments() {
  const { aRole } = useAuth();
  // Tout le référentiel est chargé une fois, filtré à l'écran : la file des
  // lignes à vérifier ne doit pas disparaître quand on regarde les actions.
  const { donnees, chargement, erreur, recharger } = useRessource<Instrument[]>('/instruments');
  const [type, setType] = useState('');
  const [recherche, setRecherche] = useState('');
  // La tuile « Lignes BDT à vérifier » arrive filtrée sur A_COMPLETER.
  const [requete] = useSearchParams();
  const [statut, setStatut] = useState(() => requete.get('statut') ?? '');

  const [csv, setCsv] = useState('');
  const [rapport, setRapport] = useState<RapportImport | null>(null);
  const [erreurImport, setErreurImport] = useState<string | null>(null);
  const [importEnCours, setImportEnCours] = useState(false);

  // Modal de saisie unitaire.
  const [edition, setEdition] = useState<string | null>(null);
  const [saisie, setSaisie] = useState(vide);
  const [erreurForm, setErreurForm] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const enCreation = edition === '';

  const tous = donnees ?? [];
  const filtresActifs = type !== '' || recherche !== '' || statut !== '';
  const instruments = tous.filter(
    (i) =>
      (!type || i.type === type) &&
      (!statut || i.statut === statut) &&
      correspond(recherche, i.code, i.isin, i.libelle, i.action?.ticker, i.opcvm?.societeGestion),
  );
  const peutGerer = aRole('ADMIN', 'MIDDLE_BO');

  const aVerifier = useMemo(() => tous.filter((i) => i.statut === 'A_COMPLETER'), [tous]);
  const [erreurVerif, setErreurVerif] = useState<string | null>(null);

  /**
   * Clôt la vérification d'une ligne découverte dans une situation reçue.
   * Sans ce geste, la file ne se vide jamais et le référentiel garde des
   * caractéristiques que personne n'a confrontées à l'avis du partenaire.
   */
  async function verifier(instrument: Instrument) {
    setErreurVerif(null);
    try {
      await api.post(`/instruments/${instrument.id}/verifier`, {});
      await recharger();
    } catch (e) {
      setErreurVerif(e instanceof ErreurApi ? messageErreur(e) : 'Vérification impossible.');
    }
  }

  const champ = (cle: keyof Saisie) => ({
    value: String(saisie[cle]),
    onChange: (e: { target: { value: string } }) => setSaisie({ ...saisie, [cle]: e.target.value }),
  });

  function ouvrirCreation() {
    setSaisie(vide());
    setEdition('');
    setErreurForm(null);
  }
  function ouvrirEdition(i: Instrument) {
    setSaisie({
      ...vide(),
      type: i.type,
      code: i.code,
      isin: i.isin ?? '',
      libelle: i.libelle,
      chariaCompliant: i.chariaCompliant,
      statut: i.statut,
      ticker: i.action?.ticker ?? '',
      secteur: i.action?.secteur ?? '',
      quotite: i.action ? String(i.action.quotite) : '',
      // La date d'émission n'est pas renvoyée par la liste : la laisser vide
      // en édition, sinon la valeur du jour écraserait la vraie à
      // l'enregistrement — un champ vide est envoyé `undefined`, donc ignoré.
      dateEmission: '',
      dateEcheance: i.bdt ? i.bdt.dateEcheance.slice(0, 10) : '',
      tauxFacial: i.bdt ? new Decimal(i.bdt.tauxFacial).toString() : '',
      nominalUnitaire: i.bdt ? new Decimal(i.bdt.nominalUnitaire).toString() : '',
      societeGestion: i.opcvm?.societeGestion ?? '',
      categorie: i.opcvm?.categorie ?? 'MONETAIRE',
      montantMinSouscription: i.opcvm ? new Decimal(i.opcvm.montantMinSouscription).toString() : '',
      fondsParDefaut: i.opcvm?.fondsParDefaut ?? false,
    });
    setEdition(i.id);
    setErreurForm(null);
  }
  function fermer() {
    setEdition(null);
  }

  async function enregistrer(evenement: FormEvent) {
    evenement.preventDefault();
    setErreurForm(null);
    setOccupe(true);
    try {
      const base = {
        code: saisie.code,
        isin: saisie.isin || undefined,
        libelle: saisie.libelle,
        chariaCompliant: saisie.chariaCompliant,
      };
      let specifique: Record<string, unknown> = {};
      if (saisie.type === 'ACTION') {
        specifique = {
          ticker: saisie.ticker || undefined,
          secteur: saisie.secteur || undefined,
          quotite: saisie.quotite ? Number(saisie.quotite) : undefined,
        };
      } else if (saisie.type === 'BDT') {
        specifique = {
          dateEmission: saisie.dateEmission || undefined,
          dateEcheance: saisie.dateEcheance || undefined,
          tauxFacial: saisie.tauxFacial || undefined,
          periodiciteCoupon: saisie.periodiciteCoupon || undefined,
          nominalUnitaire: saisie.nominalUnitaire || undefined,
        };
      } else {
        specifique = {
          societeGestion: saisie.societeGestion || undefined,
          categorie: saisie.categorie,
          montantMinSouscription: saisie.montantMinSouscription || undefined,
          fractionnement: saisie.fractionnement,
          fondsParDefaut: saisie.fondsParDefaut,
        };
      }
      const corps = { ...base, type: saisie.type, ...specifique };
      if (enCreation) {
        await api.post('/instruments', corps);
      } else {
        await api.patch(`/instruments/${edition}`, { ...corps, statut: saisie.statut });
      }
      fermer();
      await recharger();
    } catch (e) {
      setErreurForm(e instanceof ErreurApi ? messageErreur(e) : 'Enregistrement impossible.');
    } finally {
      setOccupe(false);
    }
  }

  const colonnes: Colonne<Instrument>[] = [
    { cle: 'code', entete: 'Code', num: true, rendu: (i) => i.code },
    { cle: 'libelle', entete: 'Libellé', rendu: (i) => i.libelle },
    { cle: 'isin', entete: 'ISIN', num: true, rendu: (i) => i.isin ?? '—' },
    { cle: 'type', entete: 'Type', rendu: (i) => i.type },
    {
      cle: 'detail',
      entete: 'Caractéristiques',
      rendu: (i) => {
        if (i.action) return i.action.secteur ?? '—';
        if (i.opcvm)
          return `${LIBELLE_CATEGORIE_OPCVM[i.opcvm.categorie]} · ${i.opcvm.societeGestion}`;
        if (i.bdt)
          return `Échéance ${new Date(i.bdt.dateEcheance).toLocaleDateString('fr-FR')}${
            i.trancheCourante ? ` · tranche ${i.trancheCourante}` : ''
          }`;
        return '—';
      },
    },
    {
      cle: 'charia',
      entete: 'Charia',
      rendu: (i) => (i.chariaCompliant ? <Pastille ton="interface">Conforme</Pastille> : '—'),
    },
    {
      cle: 'defaut',
      entete: 'Par défaut',
      rendu: (i) => (i.opcvm?.fondsParDefaut ? <Pastille ton="neutre">Proposé</Pastille> : '—'),
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (i) => (
        <Pastille ton={TON_STATUT[i.statut] ?? 'neutre'}>
          {LIBELLE_STATUT[i.statut] ?? i.statut}
        </Pastille>
      ),
    },
    ...(peutGerer
      ? [
          {
            cle: 'actions',
            entete: '',
            num: true,
            rendu: (i: Instrument) => (
              <>
                {i.statut === 'A_COMPLETER' && (
                  <button
                    type="button"
                    className="tableau__action"
                    onClick={() => void verifier(i)}
                    aria-label={`Marquer ${i.libelle} vérifiée`}
                    title="Marquer vérifiée"
                  >
                    <Icone nom="controle" taille={16} />
                  </button>
                )}
                <button
                  type="button"
                  className="tableau__action"
                  onClick={() => ouvrirEdition(i)}
                  aria-label={`Éditer ${i.libelle}`}
                >
                  <Icone nom="editer" taille={16} />
                </button>
              </>
            ),
          },
        ]
      : []),
  ];

  async function importer() {
    setErreurImport(null);
    setRapport(null);
    setImportEnCours(true);
    try {
      setRapport(await api.post<RapportImport>('/instruments/import', { contenu: csv }));
      await recharger();
    } catch (e) {
      setErreurImport(e instanceof ErreurApi ? messageErreur(e) : 'Import impossible.');
    } finally {
      setImportEnCours(false);
    }
  }

  return (
    <>
      <p className="entete__fil">Référentiels</p>
      <div className="entete">
        <h1>Instruments</h1>
        <span className="entete__ref">{tous.length} lignes</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}

      {erreurVerif && (
        <BandeauAlerte nature="refus" titre="Vérification impossible">
          {erreurVerif}
        </BandeauAlerte>
      )}

      {aVerifier.length > 0 && (
        <BandeauAlerte nature="gestion" titre="Lignes BDT à vérifier">
          <Nombre valeur={String(aVerifier.length)} /> ligne(s) découverte(s) dans une{' '}
          <Link to="/import-situation">situation reçue</Link> : la salle des marchés choisit la
          ligne servie, l’application ne fait que la constater. Complétez leurs caractéristiques,
          puis marquez-les vérifiées — jusque-là, elles servent à valoriser sans que personne les
          ait confrontées à l’avis du partenaire.
        </BandeauAlerte>
      )}

      <Bloc
        titre="Référentiel"
        meta={
          chargement
            ? 'chargement…'
            : filtresActifs
              ? `${instruments.length} sur ${tous.length}`
              : `${tous.length} instruments`
        }
        actions={
          peutGerer && (
            <button
              type="button"
              className="bouton bouton--icone bouton--compact"
              onClick={ouvrirCreation}
            >
              <Icone nom="creer" taille={15} />
              Nouvel instrument
            </button>
          )
        }
      >
        <BarreFiltres>
          <FiltreSegments
            libelle="Type"
            valeur={type}
            onChange={setType}
            options={[['', 'Tous'], ...TYPES_INSTRUMENT.map((t) => [t, t] as const)]}
          />
        </BarreFiltres>
        <BarreFiltres
          actifs={filtresActifs}
          onRetirer={() => {
            setType('');
            setRecherche('');
            setStatut('');
          }}
        >
          <FiltreRecherche
            valeur={recherche}
            onChange={setRecherche}
            placeholder="Code, ISIN, libellé, ticker"
          />
          <FiltreChoix
            libelle="Statut"
            tous="Tous les statuts"
            valeur={statut}
            onChange={setStatut}
            options={Object.entries(LIBELLE_STATUT)}
          />
        </BarreFiltres>

        <Tableau
          colonnes={colonnes}
          lignes={instruments}
          cle={(i) => i.id}
          vide={filtresActifs ? 'Aucun instrument ne répond à ces critères.' : 'Aucun instrument.'}
        />
      </Bloc>

      {peutGerer && (
        <Bloc titre="Import de masse" meta="CSV, séparateur point-virgule">
          <p className="consigne consigne--tete">
            En-têtes reconnus : <code>code;isin;libelle;type;charia;ticker;secteur;quotite;</code>
            <code>societe_gestion;categorie;date_emission;date_echeance;taux_facial</code>. Une
            ligne fautive n’annule jamais les autres.
          </p>
          <textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            rows={6}
            placeholder="code;isin;libelle;type;…"
            className="zone-texte"
          />
          <button
            className="bouton espace-avant-serre"
            disabled={importEnCours || csv.trim().length === 0}
            onClick={() => void importer()}
          >
            {importEnCours ? 'Import…' : 'Importer'}
          </button>

          {erreurImport && (
            <div className="espace-avant">
              <BandeauAlerte nature="refus" titre="Import refusé">
                {erreurImport}
              </BandeauAlerte>
            </div>
          )}

          {rapport && (
            <div className="espace-avant">
              <BandeauAlerte
                nature={rapport.ignores > 0 ? 'gestion' : 'information'}
                titre="Rapport d’import"
              >
                {rapport.lignes} ligne(s) lue(s), {rapport.crees} créée(s), {rapport.ignores}{' '}
                ignorée(s).
              </BandeauAlerte>
              {rapport.anomalies.length > 0 && (
                <Tableau
                  colonnes={[
                    { cle: 'ligne', entete: 'Ligne', num: true, rendu: (a) => a.ligne },
                    { cle: 'code', entete: 'Code', rendu: (a) => a.code },
                    { cle: 'message', entete: 'Motif', rendu: (a) => a.message },
                  ]}
                  lignes={rapport.anomalies}
                  cle={(a) => String(a.ligne)}
                />
              )}
            </div>
          )}
        </Bloc>
      )}

      <Modale
        ouverte={edition !== null}
        onFermer={fermer}
        surtitre="Instrument"
        titre={enCreation ? 'Nouvel instrument' : 'Modifier l’instrument'}
        taille="large"
      >
        {erreurForm && (
          <BandeauAlerte nature="refus" titre="Enregistrement refusé">
            {erreurForm}
          </BandeauAlerte>
        )}
        <form onSubmit={(e) => void enregistrer(e)} noValidate>
          <div className="champ">
            <label className="surtitre">Type</label>
            <select
              className="champ__select"
              value={saisie.type}
              disabled={!enCreation}
              onChange={(e) => setSaisie({ ...saisie, type: e.target.value as TypeInstrument })}
            >
              {TYPES_INSTRUMENT.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {!enCreation && (
              <span className="champ__aide">Le type d’un instrument est immuable.</span>
            )}
          </div>

          <div className="champs-en-ligne">
            <Champ label="Code" {...champ('code')} placeholder="ATW" required />
            <Champ label="ISIN" {...champ('isin')} placeholder="MA0000012445" maxLength={12} />
          </div>
          <Champ label="Libellé" {...champ('libelle')} placeholder="Attijariwafa Bank" required />

          {saisie.type === 'ACTION' && (
            <div className="champs-en-ligne">
              <Champ label="Ticker" {...champ('ticker')} placeholder="ATW" />
              <Champ label="Secteur" {...champ('secteur')} placeholder="Banques" />
              <Champ label="Quotité" montant {...champ('quotite')} placeholder="1" />
            </div>
          )}

          {saisie.type === 'BDT' && (
            <>
              <div className="champs-en-ligne">
                <Champ label="Date d’émission" type="date" {...champ('dateEmission')} />
                <Champ label="Date d’échéance" type="date" {...champ('dateEcheance')} />
              </div>
              <div className="champs-en-ligne">
                <Champ
                  label="Taux facial (%)"
                  montant
                  {...champ('tauxFacial')}
                  placeholder="3.25"
                />
                <Champ
                  label="Nominal unitaire"
                  montant
                  {...champ('nominalUnitaire')}
                  placeholder="100000"
                />
                <Champ
                  label="Périodicité coupon"
                  {...champ('periodiciteCoupon')}
                  placeholder="ANNUEL"
                />
              </div>
            </>
          )}

          {saisie.type === 'OPCVM' && (
            <>
              <div className="champs-en-ligne">
                <Champ
                  label="Société de gestion"
                  {...champ('societeGestion')}
                  placeholder="CDG Capital Gestion"
                />
                <div className="champ">
                  <label className="surtitre">Catégorie</label>
                  <select
                    className="champ__select"
                    value={saisie.categorie}
                    onChange={(e) =>
                      setSaisie({ ...saisie, categorie: e.target.value as CategorieOpcvm })
                    }
                  >
                    {CATEGORIES_OPCVM.map((c) => (
                      <option key={c} value={c}>
                        {LIBELLE_CATEGORIE_OPCVM[c]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <Champ
                label="Montant minimum de souscription"
                montant
                {...champ('montantMinSouscription')}
                placeholder="1000"
              />
              <label className="case-a-cocher">
                <input
                  type="checkbox"
                  checked={saisie.fractionnement}
                  onChange={(e) => setSaisie({ ...saisie, fractionnement: e.target.checked })}
                />
                Souscription fractionnable
              </label>
              <label className="case-a-cocher">
                <input
                  type="checkbox"
                  checked={saisie.fondsParDefaut}
                  onChange={(e) => setSaisie({ ...saisie, fondsParDefaut: e.target.checked })}
                />
                Fonds proposé par défaut pour sa catégorie
              </label>
            </>
          )}

          <label className="case-a-cocher">
            <input
              type="checkbox"
              checked={saisie.chariaCompliant}
              onChange={(e) => setSaisie({ ...saisie, chariaCompliant: e.target.checked })}
            />
            Conforme à la charia
          </label>

          {!enCreation && (
            <div className="champ">
              <label className="surtitre">Statut</label>
              <select
                className="champ__select"
                value={saisie.statut}
                onChange={(e) => setSaisie({ ...saisie, statut: e.target.value })}
              >
                {STATUTS_INSTRUMENT.map((s) => (
                  <option key={s} value={s}>
                    {LIBELLE_STATUT[s] ?? s}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="modale__actions">
            <button type="button" className="bouton bouton--fantome" onClick={fermer}>
              Annuler
            </button>
            <button
              className="bouton"
              type="submit"
              disabled={occupe || !saisie.code.trim() || !saisie.libelle.trim()}
            >
              {occupe ? 'Enregistrement…' : enCreation ? 'Créer l’instrument' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </Modale>
    </>
  );
}
